import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginWithPIN } from '../../services/pinAuthService';
import styles from './PINLogin.module.css';

interface PINLoginProps {
  requireAdmin?: boolean;
  onSuccess?: () => void;
}

export const PINLogin: React.FC<PINLoginProps> = ({ requireAdmin = false, onSuccess }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { data, error: loginError } = await loginWithPIN(pin);

    if (loginError || !data) {
      setError('Invalid PIN');
      setLoading(false);
      return;
    }

    // Check if admin is required
    if (requireAdmin && !data.is_admin) {
      setError('Admin access required');
      setLoading(false);
      return;
    }

    // Success - navigate based on role
    if (data.is_admin) {
      navigate('/admin', { replace: true });
    } else {
      navigate('/employee', { replace: true });
    }

    onSuccess?.();
    setLoading(false);
  };

  const handlePINChange = (value: string) => {
    // Only allow numbers, max 4 digits
    const numeric = value.replace(/\D/g, '').slice(0, 4);
    setPin(numeric);
    setError(null);
  };

  const handleNumberClick = (num: string) => {
    if (pin.length < 4) {
      setPin(pin + num);
      setError(null);
    }
  };

  const handleClear = () => {
    setPin('');
    setError(null);
  };

  const handleBackspace = () => {
    setPin(pin.slice(0, -1));
    setError(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.loginCard}>
        <div className={styles.header}>
          <h1>REFUGE</h1>
          <p>{requireAdmin ? 'ADMIN LOGIN' : 'EMPLOYEE LOGIN'}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* PIN Display */}
          <div className={styles.pinDisplay}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`${styles.pinDot} ${pin.length > i ? styles.filled : ''}`}
              >
                {pin.length > i && '•'}
              </div>
            ))}
          </div>

          {error && <div className={styles.error}>{error}</div>}

          {/* Numpad */}
          <div className={styles.numpad}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleNumberClick(num.toString())}
                className={styles.numButton}
                disabled={loading}
              >
                {num}
              </button>
            ))}

            <button
              type="button"
              onClick={handleClear}
              className={styles.actionButton}
              disabled={loading}
            >
              CLEAR
            </button>

            <button
              type="button"
              onClick={() => handleNumberClick('0')}
              className={styles.numButton}
              disabled={loading}
            >
              0
            </button>

            <button
              type="button"
              onClick={handleBackspace}
              className={styles.actionButton}
              disabled={loading}
            >
              ⌫
            </button>
          </div>

          {/* Submit button - auto-submit when 4 digits entered */}
          {pin.length === 4 && (
            <button type="submit" className={styles.submitButton} disabled={loading}>
              {loading ? 'VERIFYING...' : 'LOGIN'}
            </button>
          )}
        </form>

        <div className={styles.footer}>
          <p>Enter your 4-digit PIN</p>
        </div>
      </div>
    </div>
  );
};
